import { useEffect, useState } from "react";
import { AppBar,Button,Stack,Box,IconButton,Drawer,List,ListItem,ListItemButton,ListItemText,Collapse,useMediaQuery} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { routes } from "../../config";
import RouteItem from "../../config";
import SubMenu from "../../components/SubMenu";
import { useNavigate } from "react-router-dom";
import { Theme } from "@mui/material/styles";
import React from "react";

function NavBar() {
  const [scrollTrigger, setScrollTrigger] = useState<boolean>(false);
  const [parentPath, setParentPath] = useState<string>("/");
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [openSubMenus, setOpenSubMenus] = useState<{ [key: string]: boolean }>({});
  const pathArray: Array<string> = window.location.pathname.split("/");
  const navigate = useNavigate();

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const handleScrollTrigger = () => {
    setScrollTrigger(window.scrollY > 0);
  };

  useEffect(() => {
    handleScrollTrigger();
    window.addEventListener("scroll", handleScrollTrigger);
    return () => {
      window.removeEventListener("scroll", handleScrollTrigger);
    };
  }, []);

  useEffect(() => {
    if (pathArray.length > 1) {
      setCurrentPath(pathArray[2] || null);
      setParentPath(pathArray.slice(1, 2).join("/"));
    }
  }, [pathArray]);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleToggleSubMenu = (key: string) => {
    setOpenSubMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMenuItemClick = (menu: RouteItem) => {
    if (menu.subRoutes) {
      handleToggleSubMenu(menu.key);
    } else {
      navigate(menu.path);
      setParentPath(menu.path);
      toggleDrawer();
    }
  };

  return (
    <AppBar
      position={scrollTrigger ? "fixed" : "static"}
      elevation={scrollTrigger ? 4 : 0}
      color="primary"
      sx={{
        borderBottom: "3px solid #fdd025",
        overflow: "auto",
        maxWidth: "100%",
      }}
    >
      {/* Hamburger Icon for Mobile */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          width: "100%",
          px: 1,
          py: 0.5,
          overflow: "auto",
          position: "relative",
        }}
      >
        {isMobile && (
          <IconButton
            edge="start"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ color: "text.secondary", pl: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Stack>

      {/* Drawer for Mobile Menu */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
        <Box
          sx={{
            width: 210,
            backgroundColor: "primary.main",
            height: "100%",
            overflowY: "auto",
          }}
          role="presentation"
          onClick={toggleDrawer}
          onKeyDown={toggleDrawer}
        >
          <IconButton
            onClick={toggleDrawer}
            sx={{
              color: "text.secondary",
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
            }}
          >
            <CloseIcon />
          </IconButton>
          <List>
            {routes
              .filter((menu: RouteItem) => menu.showInHeader)
              .map((menu: RouteItem) => (
                <React.Fragment key={menu.key}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuItemClick(menu);
                      }}
                      sx={{
                        bgcolor: "primary.main",
                        color: "text.secondary",
                        ":hover": {
                          bgcolor: "secondary.main",
                          color: "text.secondary",
                        },
                        ":focus": {
                          bgcolor: "secondary.main",
                          color: "text.secondary",
                        },
                        transition: "background-color 0.3s ease",
                      }}
                    >
                      <ListItemText primary={menu.title.toUpperCase()} />
                      {menu.subRoutes ? (
                        openSubMenus[menu.key] ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )
                      ) : null}
                    </ListItemButton>
                  </ListItem>
                  {menu.subRoutes && (
                    <Collapse
                      in={openSubMenus[menu.key]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {menu.subRoutes.map((subRoute) => (
                          <ListItem
                            key={subRoute.key}
                            sx={{ pl: 4 }}
                            disablePadding
                          >
                            <ListItemButton
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`${menu.path}/${subRoute.path}`);
                                setParentPath(menu.path);
                                toggleDrawer();
                              }}
                              sx={{
                                bgcolor: "secondary.main",
                                color: "text.secondary",
                                ":hover": {
                                  bgcolor: "secondary.light",
                                  color: "text.secondary",
                                },
                              }}
                            >
                              <ListItemText primary={subRoute.title} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              ))}
          </List>
        </Box>
      </Drawer>

      {/* Desktop Menu Items */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: "100%",
          px: 1,
          py: 0,
          overflow: "auto",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2,
            flexGrow: 1,
            justifyContent: "center",
          }}
        >
          {routes
            .filter((menu: RouteItem) => menu.showInHeader)
            .map((menu: RouteItem) => {
              if (menu.subRoutes) {
                return (
                  <SubMenu
                    key={menu.key}
                    menuItem={menu}
                    parentPath={parentPath}
                    currentPath={currentPath}
                    setParentPath={setParentPath}
                    setCurrentPath={setCurrentPath}
                  />
                );
              }

              return (
                menu.key !== "router-error" &&
                menu.key !== "router-policies" && (
                  <Button
                    key={menu.key}
                    sx={{
                      textTransform: "none",
                      textWrap: "nowrap",
                      width: "100%",
                      boxSizing: "border-box",
                      color:
                        parentPath === menu.path
                          ? "text.secondary"
                          : "text.primary",
                      bgcolor:
                        parentPath === menu.path ? "secondary" : "transparent",
                      p: 1,
                      ":hover": {
                        bgcolor: "secondary.main",
                        color: "text.secondary",
                      },
                    }}
                    onClick={() => {
                      navigate(menu.path);
                      setParentPath(menu.path);
                    }}
                  >
                    {menu.title}
                  </Button>
                )
              );
            })}
        </Box>
      </Stack>
    </AppBar>
  );
}

export default NavBar;
